import { useGetAllAbsenceSalaryMappings } from '@hr/api/endpoints'
import { calendarEventTypesQuery } from '@hr/api/index.js'
import type { SelectOption } from '@hr/common/types'
import {
	abscencesWithCategories,
	absenceNS,
	translatedCausecode,
} from '@hr/mgmtview/personalprofile/newabscencemodal/newabscencemodal.constants.js'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetEventTypesOptions } from '../../../organizationview/integrations/modals/SettingsProcountor/hooks/useGetEventTypesOptions.js'

type EventType = {
	Other: string[]
	Paid: string[]
	Unpaid: string[]
}

type AbsenceTypeOption = SelectOption & { paidness?: 'Paid' | 'Unpaid' }

const sortByLabel = (a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label)

export const useGetAbscenceTypesOptionsOnlyActive = () => {
	const [translationsLoaded, setTranslationsLoaded] = useState(false)
	const [options, setOptions] = useState<{ label: string; options: AbsenceTypeOption[] }[]>([])
	const { t, i18n } = useTranslation()

	const eventTypes = useQuery<EventType>(calendarEventTypesQuery).data || {}
	const eventTypesOptions: AbsenceTypeOption[] = useGetEventTypesOptions()
	const { data: codeList } = useGetAllAbsenceSalaryMappings()

	const activeCodes = codeList?.filter((code) => code.active).map((code) => code.absenceType)
	const activeCodesSet = new Set(activeCodes)

	useEffect(() => {
		async function fetchNamespaces() {
			await i18n.loadNamespaces([absenceNS]).then(() => {
				setTranslationsLoaded(true)
				setOptions([
					{
						label: t('WithPay', 'Palkallinen'),
						options: eventTypes.Paid.map((value) => ({
							value,
							label: `${translatedCausecode(value, t)} (${t('WithPay', 'Palkallinen').toLowerCase()})`,
							paidness: eventTypesOptions.find((eventType) => eventType.value === value)?.paidness,
						}))
							.sort(sortByLabel)
							.filter((option) => activeCodesSet.has(option.value)),
					},
					{
						label: t('WithoutPay', 'Palkaton'),
						options: eventTypes.Unpaid.map((value) => ({
							value,
							label: `${translatedCausecode(value, t)} (${t('WithoutPay', 'Palkaton').toLowerCase()})`,
							paidness: eventTypesOptions.find((eventType) => eventType.value === value)?.paidness,
						}))
							.sort(sortByLabel)
							.filter((option) => activeCodesSet.has(option.value)),
					},
					{
						label: t('Others', 'Muut'),
						options: eventTypes.Other.map((value) => ({
							value,
							label: value === 'HOURS' ? t('Hours2', 'Tunnit') : t('FLEXI', 'Saldovapaa'),
							paidness: eventTypesOptions.find((eventType) => eventType.value === value)?.paidness,
						}))
							.sort(sortByLabel)
							.filter((option) => activeCodesSet.has(option.value)),
					},
				])
			})
		}
		if (!translationsLoaded && eventTypes.Paid && codeList) {
			fetchNamespaces()
		}
	}, [eventTypesOptions.find, i18n.loadNamespaces, t, eventTypes, codeList, translationsLoaded, activeCodesSet.has])

	return { options, abscencesWithCategories }
}
